import { useMemo } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type {
  FormMetadataSection,
  ProcessedFormField,
} from "@/types/api/formSchema";
import { useFormulaFields } from "@/hooks/useFormulaFields";
import taskerService from "../../../services/taskerService";
import { useLatestFormData } from "./reviewerQueries";
import { renderFieldValue, type AuthMode } from "./VendorApplicationSummary";

const BORDER = "#E0E4EF";

/** Stable empty answer set, so the formula hook's shallow compare can settle. */
const NO_ANSWERS: Record<string, unknown> = {};

// Default hidden set is the routing metadata VmReviewPanel surfaces separately
// in the assigned-reviewers card + fields that were flagged for removal in the
// demo feedback pass. Callers that DON'T render the assigned-reviewers card
// (e.g. CmReviewPanel) should override via hiddenFieldKeys to un-hide the
// routing fields — otherwise the CM can't see who VM routed the task to.
const DEFAULT_HIDDEN_FIELDS = new Set([
  "category_manager_email",
  "biz_fin_email",
  "three_vendor_quotes_available",
  "auto_invoice_validate",
]);

type Props = {
  formId: string | undefined;
  taskId: string;
  authMode?: AuthMode;
  /** Section keys to omit — caller renders these separately (e.g. the derived
   * commercial-terms preview card in CmReviewPanel). */
  hiddenSectionKeys?: Set<string>;
  /** Override the default per-field hide list (routing emails + demo removals).
   * Pass an empty set to show everything. */
  hiddenFieldKeys?: Set<string>;
};

type SummaryRow = { field: ProcessedFormField; value: React.ReactNode };

/**
 * The rows a section actually renders: the caller's hide list applied, then
 * every field the VM did not answer dropped, then formatted.
 *
 * The unanswered check has to happen BEFORE formatValue, which renders an
 * absent boolean as "No" (ENG-27885). That is right for the vendor's own
 * optional checkboxes, where an unticked box is an answer, and wrong here:
 * every boolean on the enrichment form is required whenever it is shown, so it
 * can only ever be stored true. An absent one means the question was never put
 * to the VM — most often because the Major is exempt from the quote pack — and
 * showing "No" tells the reviewer the vendor failed a control nobody checked.
 */
function buildRows(
  fields: ProcessedFormField[],
  data: Record<string, unknown>,
  authMode: AuthMode,
  hiddenFieldKeys: Set<string>,
): SummaryRow[] {
  const rows: SummaryRow[] = [];
  for (const field of fields) {
    if (hiddenFieldKeys.has(field.name)) continue;
    const raw = data[field.name];
    if (raw == null || raw === "") continue;
    const value = renderFieldValue(field, raw, authMode);
    if (value === null) continue;
    rows.push({ field, value });
  }
  return rows;
}

function FieldRows({ rows }: { rows: SummaryRow[] }) {
  return (
    <Table
      size="small"
      sx={{
        // fixed: otherwise the percentage widths below are only hints
        tableLayout: "fixed",
        "& td": { borderColor: BORDER, wordBreak: "break-word" },
      }}
    >
      <TableBody>
        {rows.map(({ field, value }) => (
          <TableRow key={field.name}>
            {/* 70/30 for the question (labels are whole sentences); top-aligned so a short answer sits beside line 1 */}
            <TableCell
              sx={{
                width: "70%",
                color: "text.secondary",
                fontSize: 13,
                verticalAlign: "top",
              }}
            >
              {field.resolvedLabel}
            </TableCell>
            <TableCell
              sx={{
                width: "30%",
                fontSize: 13,
                fontWeight: 500,
                verticalAlign: "top",
              }}
            >
              {value}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function SectionCard({
  title,
  children,
}: {
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{ borderColor: BORDER, borderRadius: "8px", mb: 2 }}
    >
      {title && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: BORDER,
            bgcolor: "#FAFBFF",
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{title}</Typography>
        </Box>
      )}
      {children}
    </Paper>
  );
}

export function VmEnrichmentSummary({
  formId,
  taskId,
  authMode = "reviewer",
  hiddenSectionKeys,
  hiddenFieldKeys = DEFAULT_HIDDEN_FIELDS,
}: Props) {
  const schemaQuery = useQuery({
    queryKey: ["reviewer-vm-enrichment-schema", formId, taskId],
    queryFn: () =>
      taskerService.getFormSchema(formId as string, taskId, "reviewer"),
    enabled: Boolean(formId && taskId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const savedQuery = useLatestFormData(
    "reviewer-vm-enrichment-summary-data",
    taskId,
    formId,
  );

  const data = savedQuery.data ?? NO_ANSWERS;

  // Resolve the form's own conditional rules against the answers the VM saved,
  // so the summary lists the questions that were actually put to them. Without
  // this the summary is a flat dump of the whole schema and a reviewer sees
  // rows for a Major's worth of questions that never appeared on the form —
  // including stale answers from a Major the VM later changed away from.
  const { visibleFields } = useFormulaFields(schemaQuery.data?.fields, undefined, data);

  const grouped = useMemo(() => {
    const sections = (schemaQuery.data?.metadata?.sections ?? []) as
      | FormMetadataSection[]
      | [];
    const map = new Map<string, ProcessedFormField[]>();
    for (const f of visibleFields) {
      const key = f.section ?? "__ungrouped";
      const arr = map.get(key) ?? [];
      arr.push(f);
      map.set(key, arr);
    }
    return { sections, map };
  }, [schemaQuery.data?.metadata, visibleFields]);

  if (schemaQuery.isInitialLoading || savedQuery.isInitialLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!formId) return null;

  // Distinguish schema-failure from "VM filled nothing" — silent null makes
  // the two indistinguishable and a reviewer can misjudge on stale/failed
  // context. Warning severity because decision can still be recorded from
  // the vendor-side left pane.
  if (schemaQuery.isError) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Couldn&apos;t load the VM enrichment form. Retry before deciding.
      </Alert>
    );
  }

  if (!schemaQuery.data) return null;

  const { sections, map } = grouped;
  const usedKeys = new Set<string>();
  const rowsFor = (fields: ProcessedFormField[]) =>
    buildRows(fields, data, authMode, hiddenFieldKeys);

  return (
    <Box>
      {sections.map((section) => {
        usedKeys.add(section.key);
        if (hiddenSectionKeys?.has(section.key)) return null;
        // a card only when a row survives — no titled empty cards
        const rows = rowsFor(map.get(section.key) ?? []);
        if (rows.length === 0) return null;
        return (
          <SectionCard key={section.key} title={section.title}>
            <FieldRows rows={rows} />
          </SectionCard>
        );
      })}
      {Array.from(map.entries())
        .filter(([key]) => !usedKeys.has(key) && !hiddenSectionKeys?.has(key))
        .map(([key, fields]) => ({ key, rows: rowsFor(fields) }))
        .filter(({ rows }) => rows.length > 0)
        .map(({ key, rows }) => (
          <SectionCard key={key}>
            <FieldRows rows={rows} />
          </SectionCard>
        ))}
    </Box>
  );
}
